import React, { useEffect, useState, useRef } from 'react';
interface Node {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  vx: number;
  vy: number;
  brightness: number;
  targetBrightness: number;
  phase: number;
}
interface Ripple {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
  createdAt: number;
}
interface NetworkPattern {
  name: string;
  generate: (width: number, height: number, count: number) => {
    x: number;
    y: number;
  }[];
}
const patterns: Record<string, NetworkPattern> = {
  distributed: {
    name: 'Distributed',
    generate: (width, height, count) => {
      return Array.from({
        length: count
      }, () => ({
        x: Math.random() * width,
        y: Math.random() * height
      }));
    }
  },
  radial: {
    name: 'Radial',
    generate: (width, height, count) => {
      const centerX = width / 2;
      const centerY = height / 2;
      const maxRadius = Math.min(width, height) * 0.4;
      return Array.from({
        length: count
      }, (_, i) => {
        const angle = i / count * Math.PI * 2 + Math.random() * 0.5;
        const radius = Math.random() * maxRadius;
        return {
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius
        };
      });
    }
  },
  grid: {
    name: 'Grid',
    generate: (width, height, count) => {
      const cols = Math.ceil(Math.sqrt(count * (width / height)));
      const rows = Math.ceil(count / cols);
      const cellWidth = width / cols;
      const cellHeight = height / rows;
      return Array.from({
        length: count
      }, (_, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        return {
          x: col * cellWidth + cellWidth / 2 + (Math.random() - 0.5) * cellWidth * 0.3,
          y: row * cellHeight + cellHeight / 2 + (Math.random() - 0.5) * cellHeight * 0.3
        };
      });
    }
  },
  clustered: {
    name: 'Clustered',
    generate: (width, height, count) => {
      const clusterCount = 5;
      const clusters = Array.from({
        length: clusterCount
      }, () => ({
        x: Math.random() * width,
        y: Math.random() * height
      }));
      return Array.from({
        length: count
      }, () => {
        const cluster = clusters[Math.floor(Math.random() * clusterCount)];
        const spread = Math.min(width, height) * 0.15;
        return {
          x: cluster.x + (Math.random() - 0.5) * spread,
          y: cluster.y + (Math.random() - 0.5) * spread
        };
      });
    }
  },
  hierarchical: {
    name: 'Hierarchical',
    generate: (width, height, count) => {
      const levels = 5;
      const nodes: {
        x: number;
        y: number;
      }[] = [];
      for (let level = 0; level < levels; level++) {
        const nodesInLevel = Math.ceil(count / levels);
        const y = height / (levels + 1) * (level + 1);
        for (let i = 0; i < nodesInLevel && nodes.length < count; i++) {
          const spread = width * (0.8 - level * 0.1);
          const x = width / 2 + (Math.random() - 0.5) * spread;
          nodes.push({
            x,
            y
          });
        }
      }
      return nodes;
    }
  }
};
export function NeuralFieldBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<Node[]>([]);
  const mouseRef = useRef({
    x: 0,
    y: 0
  });
  const animationFrameRef = useRef<number>();
  const [currentPattern, setCurrentPattern] = useState<string>('distributed');
  const transitionProgressRef = useRef(0);
  const targetPositionsRef = useRef<{
    x: number;
    y: number;
  }[]>([]);
  const ripplesRef = useRef<Ripple[]>([]);
  const shimmerPhaseRef = useRef(0);
  const NODE_COUNT = 250;
  const CONNECTION_DISTANCE = 120;
  const MOUSE_INFLUENCE_RADIUS = 150;
  const DRIFT_AMOUNT = 4;
  const BREATHING_SPEED = 0.0008;
  const RIPPLE_SPEED = 200; // pixels per second
  const RIPPLE_MAX_RADIUS = 350;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', {
      alpha: true
    });
    if (!ctx) return;
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      // Initialize or reinitialize nodes
      if (nodesRef.current.length === 0) {
        const positions = patterns[currentPattern].generate(rect.width, rect.height, NODE_COUNT);
        nodesRef.current = positions.map(pos => ({
          x: pos.x,
          y: pos.y,
          baseX: pos.x,
          baseY: pos.y,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          brightness: 0.3 + Math.random() * 0.2,
          targetBrightness: 0.3 + Math.random() * 0.2,
          phase: Math.random() * Math.PI * 2
        }));
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    // Mouse move handler
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };
    window.addEventListener('mousemove', handleMouseMove);
    // Click handler for ripples
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Only create ripples for CTA buttons
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        ripplesRef.current.push({
          x,
          y,
          radius: 0,
          maxRadius: RIPPLE_MAX_RADIUS,
          opacity: 0.6,
          createdAt: Date.now()
        });
      }
    };
    window.addEventListener('click', handleClick);
    // Scroll handler for pattern morphing and section ripples
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      // Define section thresholds
      const sections = [{
        threshold: 0,
        pattern: 'distributed'
      }, {
        threshold: windowHeight * 0.8,
        pattern: 'radial'
      }, {
        threshold: windowHeight * 2,
        pattern: 'grid'
      }, {
        threshold: windowHeight * 3.5,
        pattern: 'clustered'
      }, {
        threshold: windowHeight * 5,
        pattern: 'hierarchical'
      }];
      let newPattern = 'distributed';
      for (const section of sections) {
        if (scrollY >= section.threshold) {
          newPattern = section.pattern;
        }
      }
      if (newPattern !== currentPattern) {
        setCurrentPattern(newPattern);
        const rect = canvas.getBoundingClientRect();
        targetPositionsRef.current = patterns[newPattern].generate(rect.width, rect.height, NODE_COUNT);
        transitionProgressRef.current = 0;
        // Create a subtle ripple at center when pattern changes
        ripplesRef.current.push({
          x: rect.width / 2,
          y: rect.height / 2,
          radius: 0,
          maxRadius: RIPPLE_MAX_RADIUS * 0.7,
          opacity: 0.3,
          createdAt: Date.now()
        });
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    // Intersection Observer for section-entry ripples
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px'
    };
    const sectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const rect = canvas.getBoundingClientRect();
          const sectionRect = entry.target.getBoundingClientRect();
          const x = rect.width / 2;
          const y = sectionRect.top - rect.top + sectionRect.height / 2;
          // Create subtle ripple when section enters view
          ripplesRef.current.push({
            x,
            y: Math.max(0, Math.min(rect.height, y)),
            radius: 0,
            maxRadius: RIPPLE_MAX_RADIUS * 0.5,
            opacity: 0.2,
            createdAt: Date.now()
          });
        }
      });
    }, observerOptions);
    // Observe all sections
    const sections = document.querySelectorAll('section');
    sections.forEach(section => sectionObserver.observe(section));
    let lastTime = Date.now();
    // Animation loop
    const animate = () => {
      if (!ctx || !canvas) return;
      const currentTime = Date.now();
      const deltaTime = (currentTime - lastTime) / 1000; // seconds
      lastTime = currentTime;
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      // Draw gradient background
      const gradient = ctx.createLinearGradient(0, 0, 0, rect.height);
      gradient.addColorStop(0, '#0a0e1a');
      gradient.addColorStop(1, '#1a1f2e');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, rect.width, rect.height);
      const nodes = nodesRef.current;
      const mouse = mouseRef.current;
      // Update shimmer phase for subtle horizontal distortion
      shimmerPhaseRef.current += 0.002;
      // Update ripples
      ripplesRef.current = ripplesRef.current.filter(ripple => {
        const age = (currentTime - ripple.createdAt) / 1000;
        ripple.radius += RIPPLE_SPEED * deltaTime;
        ripple.opacity = Math.max(0, 0.6 - age * 0.4);
        return ripple.radius < ripple.maxRadius && ripple.opacity > 0;
      });
      // Update and draw nodes
      nodes.forEach((node, i) => {
        if (prefersReducedMotion) {
          node.x = node.baseX;
          node.y = node.baseY;
          node.brightness = 0.4;
          return;
        }
        // Pattern transition
        if (transitionProgressRef.current < 1 && targetPositionsRef.current.length > 0) {
          transitionProgressRef.current = Math.min(1, transitionProgressRef.current + 0.008);
          const eased = easeInOutCubic(transitionProgressRef.current);
          const target = targetPositionsRef.current[i];
          node.baseX = node.baseX + (target.x - node.baseX) * eased * 0.1;
          node.baseY = node.baseY + (target.y - node.baseY) * eased * 0.1;
        }
        // Breathing animation
        node.phase += BREATHING_SPEED;
        const drift = Math.sin(node.phase) * DRIFT_AMOUNT;
        const driftAngle = node.phase * 0.5;
        // Subtle horizontal shimmer distortion
        const shimmerOffset = Math.sin(shimmerPhaseRef.current + node.y * 0.01) * 2;
        node.x = node.baseX + Math.cos(driftAngle) * drift + shimmerOffset;
        node.y = node.baseY + Math.sin(driftAngle) * drift;
        // Brightness breathing
        node.targetBrightness = 0.3 + Math.sin(node.phase * 0.7) * 0.15;
        // Ripple displacement effect
        ripplesRef.current.forEach(ripple => {
          const dx = node.x - ripple.x;
          const dy = node.y - ripple.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const rippleEdge = ripple.radius;
          const rippleWidth = 40;
          // Only displace nodes near the ripple edge
          if (distance > rippleEdge - rippleWidth && distance < rippleEdge + rippleWidth) {
            const influence = 1 - Math.abs(distance - rippleEdge) / rippleWidth;
            const displacement = influence * ripple.opacity * 2;
            node.x += dx / distance * displacement;
            node.y += dy / distance * displacement;
            node.targetBrightness = Math.max(node.targetBrightness, 0.5 + influence * 0.3);
          }
        });
        // Mouse interaction
        const mdx = mouse.x - node.x;
        const mdy = mouse.y - node.y;
        const distance = Math.sqrt(mdx * mdx + mdy * mdy);
        if (distance < MOUSE_INFLUENCE_RADIUS) {
          const influence = 1 - distance / MOUSE_INFLUENCE_RADIUS;
          node.targetBrightness = Math.max(node.targetBrightness, 0.6 + influence * 0.2);
          const pullStrength = influence * 0.15;
          node.x += mdx * pullStrength;
          node.y += mdy * pullStrength;
        }
        // Smooth brightness transition
        node.brightness += (node.targetBrightness - node.brightness) * 0.05;
      });
      // Draw ripples
      ripplesRef.current.forEach(ripple => {
        // Ensure inner radius is never negative
        const innerRadius = Math.max(0, ripple.radius - 20);
        const outerRadius = ripple.radius + 20;
        const gradient = ctx.createRadialGradient(ripple.x, ripple.y, innerRadius, ripple.x, ripple.y, outerRadius);
        gradient.addColorStop(0, `rgba(100, 200, 255, 0)`);
        gradient.addColorStop(0.5, `rgba(100, 200, 255, ${ripple.opacity * 0.3})`);
        gradient.addColorStop(1, `rgba(150, 100, 255, 0)`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.stroke();
      });
      // Draw connections
      ctx.strokeStyle = 'rgba(100, 150, 255, 0.15)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < CONNECTION_DISTANCE) {
            const opacity = (1 - distance / CONNECTION_DISTANCE) * 0.2;
            ctx.strokeStyle = `rgba(100, 150, 255, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      // Draw nodes
      nodes.forEach(node => {
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, 3);
        gradient.addColorStop(0, `rgba(150, 200, 255, ${node.brightness})`);
        gradient.addColorStop(1, `rgba(100, 150, 255, 0)`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
      sectionObserver.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [currentPattern]);
  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none" style={{
    zIndex: 0
  }} data-id="element-312" />;
}
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}